import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useLoaderData, Link, useSearchParams } from "@remix-run/react";
import { useState } from "react";
import { ArticleService } from "~/services/article.server";
import { Card } from "~/components/ui/Card";
import { Button } from "~/components/ui/Button";
import { ArticleCard } from "~/components/articles/ArticleCard";

// Article category mapping
const CATEGORY_LABELS = {
  "slow-aging": "저속노화",
  "health-info": "건강정보",
  ingredients: "식재료정보",
  nutrition: "영양정보",
  lifestyle: "라이프스타일",
} as const;

const CATEGORY_DESCRIPTIONS = {
  "slow-aging": "노화를 늦추는 식단과 생활습관에 대한 정보를 제공합니다",
  "health-info": "건강한 삶을 위한 다양한 정보와 팁을 확인하세요",
  ingredients: "건강에 좋은 식재료의 효능과 활용법을 알아보세요",
  nutrition: "영양소와 균형잡힌 식단에 대한 전문 정보를 제공합니다",
  lifestyle: "건강한 라이프스타일을 위한 실용적인 조언을 드립니다",
} as const;

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  if (!data?.category || !(data.category in CATEGORY_LABELS)) {
    return [
      { title: "카테고리를 찾을 수 없습니다 - 슬로우다이브" },
      { name: "description", content: "요청하신 카테고리를 찾을 수 없습니다." },
    ];
  }

  const categoryLabel =
    CATEGORY_LABELS[data.category as keyof typeof CATEGORY_LABELS];
  const categoryDescription =
    CATEGORY_DESCRIPTIONS[data.category as keyof typeof CATEGORY_DESCRIPTIONS];
  const title = `${categoryLabel} - 슬로우다이브`;

  return [
    { title },
    { name: "description", content: categoryDescription },
    { property: "og:title", content: title },
    { property: "og:description", content: categoryDescription },
    { property: "og:type", content: "website" },
  ];
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { category } = params;

  if (!category || !(category in CATEGORY_LABELS)) {
    throw new Response("Category not found", { status: 404 });
  }

  const url = new URL(request.url);
  const query = url.searchParams.get("query") || undefined;
  const page = parseInt(url.searchParams.get("page") || "1", 10);
  const limit = 12; // Articles per page

  try {
    const result = await ArticleService.searchArticles({
      query,
      category,
      page,
      limit,
      publishedOnly: true,
    });

    return json({
      articles: result.articles,
      pagination: result.pagination,
      category,
      currentQuery: query,
    });
  } catch (error) {
    console.error("Failed to load articles:", error);
    throw new Response("Failed to load articles", { status: 500 });
  }
}

export default function ArticleCategory() {
  const { articles, pagination, category, currentQuery } =
    useLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(currentQuery || "");

  const categoryLabel =
    CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS];
  const categoryDescription =
    CATEGORY_DESCRIPTIONS[category as keyof typeof CATEGORY_DESCRIPTIONS];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);

    if (searchQuery.trim()) {
      newParams.set("query", searchQuery.trim());
    } else {
      newParams.delete("query");
    }

    newParams.delete("page"); // Reset to first page
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <li>
              <Link
                to="/articles"
                className="hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                건강정보
              </Link>
            </li>
            <li>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </li>
            <li className="text-gray-900 dark:text-gray-100 font-medium">
              {categoryLabel}
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {categoryLabel}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {categoryDescription}
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`${categoryLabel} 아티클 검색...`}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <Button type="submit" variant="primary">
                검색
              </Button>
            </div>
          </form>
        </div>

        {/* Results Info */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {categoryLabel} 카테고리에서 총 {pagination.total}개의 아티클을
            찾았습니다
            {currentQuery && ` (검색어: "${currentQuery}")`}
          </p>
        </div>

        {/* Articles Grid */}
        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                categoryLabel={categoryLabel}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-600 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              아티클을 찾을 수 없습니다
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {currentQuery
                ? "검색 조건을 변경해보세요."
                : `${categoryLabel} 카테고리에 아직 아티클이 없습니다.`}
            </p>
            <div className="space-x-4">
              {currentQuery && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchParams({});
                  }}
                >
                  검색 초기화
                </Button>
              )}
              <Link to="/articles">
                <Button variant="primary">전체 아티클 보기</Button>
              </Link>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              이전
            </Button>

            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((page) => {
                const current = pagination.page;
                return (
                  page === 1 ||
                  page === pagination.totalPages ||
                  (page >= current - 2 && page <= current + 2)
                );
              })
              .map((page, index, array) => {
                const prevPage = array[index - 1];
                const showEllipsis = prevPage && page - prevPage > 1;

                return (
                  <div key={page} className="flex items-center">
                    {showEllipsis && (
                      <span className="px-2 text-gray-500">...</span>
                    )}
                    <Button
                      variant={
                        pagination.page === page ? "primary" : "secondary"
                      }
                      size="sm"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  </div>
                );
              })}

            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              다음
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
